/**
 * Multi-modal Test Modal (Phase 5)
 *
 * Compact modal that lets a user upload an image/audio/video file plus a
 * text prompt, then invoke a centralized agent via the multimodal endpoint.
 *
 * Opened from the AgentDetailModal when an agent has non-text modalities in
 * its supported_modalities list. Uses apiClient.invokeCentralizedAgentMultimodal.
 */

import { useState, useRef } from 'react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'
import { apiClient } from '@/services/api'
import {
  PhotoIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
  PlayIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

interface MultimodalTestModalProps {
  isOpen: boolean
  onClose: () => void
  agentName: string
  displayName: string
  supportedModalities: string[]
}

export default function MultimodalTestModal({
  isOpen,
  onClose,
  agentName,
  displayName,
  supportedModalities,
}: MultimodalTestModalProps) {
  const [prompt, setPrompt] = useState('')
  const [contextJson, setContextJson] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<any>(null)

  const imageRef = useRef<HTMLInputElement>(null)
  const audioRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLInputElement>(null)

  const supportsImage = supportedModalities.includes('image')
  const supportsAudio =
    supportedModalities.includes('voice') || supportedModalities.includes('audio')
  const supportsVideo = supportedModalities.includes('video')

  const canSubmit =
    prompt.trim().length > 0 &&
    (imageFile !== null || audioFile !== null || videoFile !== null) &&
    !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return

    let context: Record<string, any> | undefined
    if (contextJson.trim()) {
      try {
        context = JSON.parse(contextJson)
      } catch {
        toast.error('Context JSON is invalid')
        return
      }
    }

    setSubmitting(true)
    setResult(null)
    try {
      const res = await apiClient.invokeCentralizedAgentMultimodal(agentName, {
        prompt,
        context,
        image: imageFile,
        audio: audioFile,
        video: videoFile,
      })
      setResult(res)
      const success =
        res?.success === true ||
        res?.result?.success === true ||
        res?.structured_data?.success === true
      if (success) {
        toast.success('Agent executed successfully')
      } else {
        toast.error('Agent returned an error — see result panel')
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      const msg = typeof detail === 'string' ? detail : detail?.error || e.message
      toast.error(`Request failed: ${msg}`)
      setResult({ error: msg })
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    setPrompt('')
    setContextJson('')
    setImageFile(null)
    setAudioFile(null)
    setVideoFile(null)
    setResult(null)
    onClose()
  }

  // Extract text output from various response shapes
  const outputText: string | null =
    result?.content ||
    result?.result?.output ||
    result?.structured_data?.output ||
    (typeof result?.result === 'string' ? result.result : null)

  const copyOutput = () => {
    if (outputText) {
      navigator.clipboard.writeText(outputText)
      toast.success('Output copied to clipboard')
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Test ${displayName} (multi-modal)`}
      description={`Upload a file and enter a prompt. Supported modalities: ${supportedModalities.join(', ')}`}
      size="3xl"
    >
      <div className="space-y-4">
        {/* Prompt */}
        <div>
          <label className="mb-1 block text-sm font-medium">Prompt</label>
          <textarea
            rows={3}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800"
            placeholder="What do you want the agent to do with the uploaded file?"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        {/* File uploaders — shown only for supported modalities */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {supportsImage && (
            <FileCard
              label="Image"
              icon={<PhotoIcon className="h-6 w-6" />}
              accept="image/png,image/jpeg,image/webp,image/gif"
              file={imageFile}
              onChange={setImageFile}
              inputRef={imageRef}
              examples="png, jpeg, webp"
            />
          )}
          {supportsAudio && (
            <FileCard
              label="Audio"
              icon={<MusicalNoteIcon className="h-6 w-6" />}
              accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/x-m4a"
              file={audioFile}
              onChange={setAudioFile}
              inputRef={audioRef}
              examples="mp3, wav, m4a, ogg"
            />
          )}
          {supportsVideo && (
            <FileCard
              label="Video"
              icon={<VideoCameraIcon className="h-6 w-6" />}
              accept="video/mp4,video/webm,video/quicktime"
              file={videoFile}
              onChange={setVideoFile}
              inputRef={videoRef}
              examples="mp4, webm, mov"
            />
          )}
        </div>

        {/* Optional context JSON (collapsed by default) */}
        <details className="rounded-md border border-slate-200 p-2 text-sm dark:border-slate-700">
          <summary className="cursor-pointer select-none font-medium text-slate-700 dark:text-slate-300">
            Optional: context JSON
          </summary>
          <textarea
            rows={3}
            className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-xs dark:border-slate-600 dark:bg-slate-800"
            placeholder='{"video_mode": "keyframes", "video_fps": 0.2}'
            value={contextJson}
            onChange={(e) => setContextJson(e.target.value)}
          />
        </details>

        {/* Submit */}
        <div className="flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-700">
          <div className="text-xs text-slate-500">
            Max per-file size: 20 MB. Backend timeout: 10 min for uploads.
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit} isLoading={submitting}>
              <PlayIcon className="mr-1 h-4 w-4" /> Execute
            </Button>
          </div>
        </div>

        {/* Result panel */}
        {result && (
          <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold">Result</div>
              {outputText && (
                <button
                  onClick={copyOutput}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" /> Copy
                </button>
              )}
            </div>
            {outputText ? (
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-white p-2 font-mono text-xs dark:bg-slate-800">
                {outputText}
              </pre>
            ) : (
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-white p-2 font-mono text-xs dark:bg-slate-800">
                {JSON.stringify(result, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

// ── File upload card ──

function FileCard({
  label,
  icon,
  accept,
  file,
  onChange,
  inputRef,
  examples,
}: {
  label: string
  icon: React.ReactNode
  accept: string
  file: File | null
  onChange: (f: File | null) => void
  inputRef: React.RefObject<HTMLInputElement>
  examples: string
}) {
  const click = () => inputRef.current?.click()
  const clear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    if (inputRef.current) inputRef.current.value = ''
  }
  return (
    <div
      onClick={click}
      className={clsx(
        'cursor-pointer rounded-lg border-2 border-dashed p-3 transition-colors',
        file
          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20'
          : 'border-slate-300 hover:border-blue-500 dark:border-slate-600',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      <div className="flex items-center gap-2">
        {icon}
        <div className="flex-1 truncate">
          <div className="text-sm font-semibold">{label}</div>
          <div className="truncate text-xs text-slate-500">
            {file ? file.name : examples}
          </div>
          {file && (
            <div className="text-[10px] text-slate-400">
              {(file.size / 1024).toFixed(1)} KB
            </div>
          )}
        </div>
        {file && (
          <button
            onClick={clear}
            className="text-xs text-red-500 hover:text-red-700"
            title="Clear file"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  )
}
